import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ImageCropDetailCropResults from '../components/ImageCropDetailCropResults';
import ImageCropDetailMediaTile from '../components/ImageCropDetailMediaTile';
import DetailQuickToolBridgeToolbar from '@/pages/ipe/workbench/components/DetailQuickToolBridgeToolbar';
import DetailQuickToolBridgeModals from '@/pages/ipe/workbench/components/DetailQuickToolBridgeModals';
import IpeGallerySelectionBar from '@/pages/ipe/workbench/components/IpeGallerySelectionBar';
import {
  GoodsDetailQuickSelectMenu,
} from '@/pages/ipe/workbench/components/GoodsDetailQuickSelectMenu';
import { navigateDetailBack, navigateDetailToAccountCenter } from '@/pages/ipe/workbench/utils/detailPageNavigate';
import useGalleryDisabledKeys from '@/pages/ipe/workbench/hooks/useGalleryDisabledKeys';
import useMarkHistoryDetailViewed from '@/pages/ipe/workbench/hooks/useMarkHistoryDetailViewed';
import useDetailQuickToolBridge from '@/pages/ipe/workbench/hooks/useDetailQuickToolBridge';
import useListingTemplateExport from '@/pages/ipe/workbench/hooks/useListingTemplateExport';
import {
  buildSimpleImageExportRows,
  resolveSimpleExportColumnMode,
} from '@/pages/ipe/workbench/utils/buildSimpleImageExportRows';
import ImageCropAdjustModal from '../components/ImageCropAdjustModal';
import {
  resolveImageCropOriginalTileDisplayUrl,
  resolveImageCropReferencePreviewMap,
} from '../utils/imageCropReferencePreview';
import { warmImageCropDetailDisplayCache } from '../utils/imageCropDisplayWarm';
import { IpeImagePreviewOverlay, useIpeImagePreview } from '@/pages/ipe/workbench/components/IpeImagePreviewOverlay';
import { extractBboxNormalized } from '../utils/imageCropRetryRoute';
import { mergeManualCropIntoDetailRow, invalidateManualCropDisplayCache } from '../utils/imageCropManualCropMerge';
import { taskAPI, cropImageAPI } from '@/utils/api';
import IpeDownloadOptionsModal from '@/pages/ipe/workbench/modals/IpeDownloadOptionsModal';
import { performFlatImageListZipDownload } from '@/pages/ipe/workbench/utils/goodsZipDownload';
import {
  cancelImageCropTask,
  clearImageCropTaskCancelled,
  isImageCropTaskCancelled,
  shouldStopImageCropTask,
} from '../utils/imageCropTaskCancel';
import { isImageCropServerWorkerActive } from '../utils/imageCropServerWorker';
import {
  isImageCropBackgroundActive,
  stopImageCropTaskInBackground,
  registerImageCropResolveOriginalUrl,
  ensureImageCropTaskInBackground,
} from '../services/imageCropListProcessor';
import { runImageCropTaskBatch } from '../services/imageCropProcessTask';
import { withIpeProcessingSlot } from '@/pages/ipe/workbench/utils/ipeProcessingQueue';
import { computeCropTaskProgress } from '../utils/imageCropTaskStats';
import { isTaskInProgress } from '@/pages/ipe/workbench/utils/taskProgress';
import {
  loadCropImagesForTask,
  mergeDetailImageRows,
  resolveDetailRowsFromBatch,
} from '../utils/imageCropLoadResults';
import { mapCropRowToDetailRow, shouldProcessImageCrop, isTerminalCropTaskStatus, normalizeCropPosition } from '../utils/imageCropRowStatus';
import { resolveImageCropOriginalUrl } from '../utils/imageCropResolveUrl';
import { cancelIpeServerBatch } from '@/pages/ipe/workbench/utils/ipeBatchTaskCancel';
import {
  applyImageCropResultCache,
  clearImageCropResultCache,
  rememberImageCropResult,
} from '../utils/imageCropResultCache';
import { IMAGE_CROP_NAV_ID } from '../constants/imageCropConfig';
import useImageCropProgress from '../hooks/useImageCropProgress';
import { retryImageCropItem } from '../services/imageCropTaskApi';
import {
  isImageCropFatalPollError,
  presentImageCropApiError,
  resolveImageCropFailedMessage,
} from '../utils/imageCropTaskApiError';
import {
  collectCropDownloadItems,
  rowHasCropResultTiles,
  rowHasSavedCropTiles,
  resolveCropResultTiles,
  buildCropDetailOriginalSelectKey,
  collectCropDetailResultSelectableTileKeys,
  collectCropDetailQuickSelectKeysAtPosition,
  getCropDetailQuickSelectPositionCount,
  resolveCropDetailSelectionEntry,
} from '../utils/imageCropDetailPrints';
import WorkbenchDetailPageShell from '@/pages/ipe/workbench/components/WorkbenchDetailPageShell';
import WorkbenchDetailPageHero from '@/pages/ipe/workbench/components/WorkbenchDetailPageHero';
import { buildBatchTaskHeroMeta } from '@/pages/ipe/workbench/utils/workbenchDetailPageUtils';
import {
  buildDetailLoadingHint,
  loadWorkbenchDetailTaskWithRetry,
} from '@/pages/ipe/workbench/utils/workbenchDetailTaskLoader';
import { sanitizeIpeUserMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import { resolveImageUrlToBlob } from '@/utils/imageUrlUtils';
import { useArmedScrollLoadMore } from '@/hooks/useArmedScrollLoadMore';
import {
  useDetailPageLargeListMode,
  DETAIL_PAGE_LARGE_LIST_THRESHOLD,
} from '@/hooks/useDetailPageLargeListMode';
import DetailPageScrollContext from '@/contexts/DetailPageScrollContext';
import IpeLazyListSentinel from '@/components/ipe/IpeLazyListSentinel';
import {
  resolveImmediateSignedSrc,
  resolveSignedDisplayUrl,
} from '@/utils/imageDisplayAccess';
import '@/styles/ipe/workbench/GoodsDetailPage.css';
import '@/styles/ipe/workbench/Img2ImgDetailPage.css';
import '@/styles/ipe/workbench/SourcingHubPage.css';
import '@/styles/ipe/workbench/SmartCutoutRefineModal.css';
import '@/styles/shared/gallery-media-card.css';
import '../styles/ImageCropDetailPage.css';

const IMAGE_CROP_DETAIL_SHELL_CLASS = 'goods-detail-shell ai-image-detail-shell img2img-detail-shell--image-crop';
const IMAGE_CROP_DETAIL_PAGE_CLASS = 'goods-detail-page ai-image-detail-page img2img-detail-page--image-crop image-crop-detail-page';

/** 原图→裁剪结果一次性渲染过多会撑爆 DOM，效仿商品设计详情页按批增量渲染 */
const IMAGE_CROP_GROUP_BATCH_SIZE = 30;

// Toast 提示组件
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    if (!onClose) return;
    const timer = setTimeout(() => {
      try {
        onClose();
      } catch (error) {
        console.error('Toast onClose 执行失败:', error);
      }
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" fill="#4caf50"/>
            <path d="M6 10l2 2 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" fill="#f44336"/>
            <path d="M10 6v4M10 14h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" fill="#2196f3"/>
            <path d="M10 6v4M10 14h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
    }
  };

  return (
    <div className={`modern-toast modern-toast--${type}`}>
      <div className="modern-toast__icon">{getIcon()}</div>
      <span className="modern-toast__message">{message}</span>
    </div>
  );
}

export default function ImageCropDetailPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useCallback(() => {
    navigateDetailBack(navigate, IMAGE_CROP_NAV_ID, location.state);
  }, [location.state, navigate]);
  const openAccountCenter = useCallback(() => {
    navigateDetailToAccountCenter(navigate);
  }, [navigate]);
  const { taskId } = useParams();
  useMarkHistoryDetailViewed(IMAGE_CROP_NAV_ID, taskId);
  const { task: taskFromState, imageIds: imageIdsFromState } = location.state || {};
  
  const [task, setTask] = useState(taskFromState);
  const [imageData, setImageData] = useState([]); // [{ imageId, originalUrl, croppedUrl, status, error }]
  const [visibleCropCount, setVisibleCropCount] = useState(IMAGE_CROP_GROUP_BATCH_SIZE);
  const loadMoreSentinelRef = useRef(null);
  const mainScrollRef = useRef(null);
  const [mainScrollRoot, setMainScrollRoot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadPhase, setLoadPhase] = useState(taskFromState ? 'ready' : 'loading');
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState(new Set()); // 选中的磁贴 selectKey 集合
  // 图片预览相关状态
  const {
    previewImage,
    previewScale,
    setPreviewScale,
    previewContainerRef,
    openPreview,
    closePreview,
  } = useIpeImagePreview();
  // 自动处理只触发一次，避免反复抖动
  const autoProcessTriggeredRef = useRef(false);
  const terminateRequestedRef = useRef(false);
  const processingRef = useRef(false);
  const imageDataRef = useRef(imageData);
  const failedImageIdsRef = useRef(new Set());
  const [croppedLoadFailed, setCroppedLoadFailed] = useState({}); // imageId -> true 表示裁剪结果图加载失败（如 404），用于回退显示原图
  const [listWorkerActive, setListWorkerActive] = useState(false);
  const [retryingImageId, setRetryingImageId] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editConfirming, setEditConfirming] = useState(false);
  const [bulkZipBusy, setBulkZipBusy] = useState(false);
  const {
    disableKeys,
    restoreKey,
    isDisabled,
    filterEnabledKeys,
  } = useGalleryDisabledKeys();
  const [downloadTargetRows, setDownloadTargetRows] = useState(null);
  const [pendingBulkDownloadItems, setPendingBulkDownloadItems] = useState(null);
  const manualCropInFlightRef = useRef(false);

  const showToast = useCallback((message, type = 'warning') => {
    setToast({ message, type });
  }, []);

  const {
    openExportPicker,
    ListingTemplateExportModals,
    isExportUiOpen,
  } = useListingTemplateExport({ showToast });

  useEffect(() => {
    imageDataRef.current = imageData;
  }, [imageData]);

  useEffect(() => {
    processingRef.current = processing;
  }, [processing]);

  useEffect(() => {
    if (!taskId) return undefined;
    const tick = () => setListWorkerActive(isImageCropBackgroundActive(taskId));
    tick();
    const id = setInterval(tick, 800);
    return () => clearInterval(id);
  }, [taskId]);

  useEffect(() => {
    if (!taskId) return undefined;
    const onProgress = (event) => {
      const { taskType, task: patch } = event.detail || {};
      if (taskType !== IMAGE_CROP_NAV_ID || !patch?.id || String(patch.id) !== String(taskId)) return;
      setTask((prev) =>
        prev
          ? {
              ...prev,
              total: patch.total ?? prev.total,
              successCount: patch.successCount ?? prev.successCount,
              failedCount: patch.failedCount ?? prev.failedCount,
              status: patch.status ?? prev.status,
            }
          : prev,
      );
    };
    window.addEventListener('ipeTaskProgress', onProgress);
    return () => window.removeEventListener('ipeTaskProgress', onProgress);
  }, [taskId]);

  const isCropBusy = processing || listWorkerActive;

  const displayImageData = useMemo(
    () => applyImageCropResultCache(taskId, imageData),
    [taskId, imageData],
  );

  /** 与 Studio 入库 index 对齐：按详情行顺序展开已落盘裁剪结果 */
  const cropOutputIndexByTileKey = useMemo(() => {
    const map = new Map();
    let index = 0;
    displayImageData.forEach((row) => {
      if (!row || row.status === 'failed') return;
      resolveCropResultTiles(row).forEach((tile) => {
        if (!tile?.tileKey || !tile.url) return;
        map.set(tile.tileKey, index);
        index += 1;
      });
    });
    return map;
  }, [displayImageData]);

  const referencePreviewByImageId = useMemo(
    () => resolveImageCropReferencePreviewMap(task),
    [task],
  );

  useEffect(() => {
    if (!displayImageData.length) return undefined;
    let alive = true;
    void warmImageCropDetailDisplayCache(displayImageData, referencePreviewByImageId).then(() => {
      if (!alive) return;
      // sign-batch 完成后由 displayCache 订阅驱动 SignedImage 刷新
    });
    return () => {
      alive = false;
    };
  }, [displayImageData, referencePreviewByImageId]);

  const deriveTaskStatus = useCallback((total, successCount, failedCount, allProcessed) => {
    if (!allProcessed || total <= 0) return 'processing';
    if (successCount > 0 && failedCount > 0) return 'partial';
    if (failedCount >= total) return 'failed';
    return 'success';
  }, []);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  const resolveDetailRowsForImageIds = useCallback(async (batchRows, imageIds) => {
    const taskImageIds = task?.imageIds || imageIdsFromState || [];
    const referenceUrls = Array.isArray(task?.result?.referenceUrls) ? task.result.referenceUrls : [];
    const sourceImageUrls = task?.result?.sourceImageUrls && typeof task.result.sourceImageUrls === 'object'
      ? task.result.sourceImageUrls
      : {};
    return resolveDetailRowsFromBatch(batchRows, imageIds, {
      getCachedOriginalUrl: (imageId) => {
        const prev = imageDataRef.current.find((img) => String(img.imageId) === String(imageId));
        return prev?.originalUrl && String(prev.originalUrl).trim();
      },
      resolveOriginalUrl: async (imageId) => {
        const idx = taskImageIds.findIndex((id) => String(id) === String(imageId));
        if (idx >= 0 && referenceUrls[idx]) {
          return String(referenceUrls[idx]).trim();
        }
        const fromSource = String(
          sourceImageUrls[imageId]
          || sourceImageUrls[taskImageIds[idx]]
          || '',
        ).trim();
        if (fromSource) return fromSource;
        return resolveImageCropOriginalUrl(imageId);
      },
      patchRow: (row, cropData) => {
        if (!row) return row;
        if (failedImageIdsRef.current.has(row.imageId) && row.status === 'pending') {
          return { ...row, status: 'failed' };
        }
        if (!cropData) return row;
        return row;
      },
    });
  }, [task?.imageIds, task?.result?.referenceUrls, task?.result?.sourceImageUrls, imageIdsFromState]);

  const viewStats = useMemo(() => {
    const taskTotal = Number(task?.total ?? 0);
    const taskSuccess = Number(task?.successCount ?? 0);
    const taskFailed = Number(task?.failedCount ?? 0);
    const taskStatus = task?.status || 'processing';

    if (!Array.isArray(imageData) || imageData.length === 0) {
      const total = Math.max(0, taskTotal);
      const successCount = Math.max(0, taskSuccess);
      // 任务仍在处理中时，勿把接口里错误的 failedCount（未处理被算失败）展示出来
      const failedCount = isTaskInProgress(taskStatus) ? 0 : Math.max(0, taskFailed);
      const allProcessed =
        total > 0
        && !isTaskInProgress(taskStatus)
        && (successCount + Math.max(0, taskFailed)) >= total;
      return {
        total,
        successCount,
        failedCount,
        status: isTaskInProgress(taskStatus)
          ? 'processing'
          : deriveTaskStatus(total, successCount, Math.max(0, taskFailed), allProcessed),
      };
    }

    const total = imageData.length;
    const successCount = imageData.filter((img) => img?.status === 'success').length;
    const failedCount = imageData.filter((img) => img?.status === 'failed').length;
    const pendingCount = total - successCount - failedCount;
    const allProcessed = total > 0 && pendingCount <= 0;
    return {
      total,
      successCount,
      failedCount,
      status: deriveTaskStatus(total, successCount, failedCount, allProcessed),
    };
  }, [task?.total, task?.successCount, task?.failedCount, task?.status, imageData, deriveTaskStatus]);

  useEffect(() => {
    if (!taskId || !task || loading) return;
    const beforeTotal = Number(task.total ?? 0);
    const beforeSuccess = Number(task.successCount ?? 0);
    const beforeFailed = Number(task.failedCount ?? 0);
    const beforeStatus = task.status || 'processing';
    const changed =
      beforeTotal !== viewStats.total ||
      beforeSuccess !== viewStats.successCount ||
      beforeFailed !== viewStats.failedCount ||
      beforeStatus !== viewStats.status;
    // 允许把进行中任务落成失败（勿因 successCount===0 卡死「进行中」轮询）
    if (!changed) return;

    setTask((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        total: viewStats.total,
        successCount: viewStats.successCount,
        failedCount: viewStats.failedCount,
        status: viewStats.status,
      };
    });

    taskAPI.update(taskId, {
      total: viewStats.total,
      successCount: viewStats.successCount,
      failedCount: viewStats.failedCount,
      status: viewStats.status,
    }).catch((error) => {
      console.error('同步裁剪任务统计失败:', error);
    });
  }, [taskId, task, viewStats, loading]);

  // 加载任务数据
  useEffect(() => {
    let cancelled = false;

    const loadTask = async () => {
      if (!taskId) return;
      setLoadPhase('loading');

      try {
        const taskData = await loadWorkbenchDetailTaskWithRetry(async () => {
          const { task: row } = await taskAPI.getById(taskId);
          return row || null;
        }, {
          onSlowLoad: () => {
            if (!cancelled) setLoadPhase('slow');
          },
        });

        if (cancelled) return;

        if (taskData) {
          setTask({
            ...taskData,
            total: taskData.total ?? taskData.imageIds?.length ?? 0,
            successCount: taskData.successCount ?? 0,
            failedCount: taskData.failedCount ?? 0,
            status: taskData.status || 'processing',
          });
          setLoadPhase('ready');
          return;
        }

        setLoadPhase('error');
        setToast({ message: '未找到任务数据，请稍后重试或返回列表', type: 'error' });
      } catch (error) {
        if (cancelled) return;
        console.error('加载任务数据失败:', error);
        setLoadPhase('error');
        setToast({ message: '加载任务失败，请稍后重试或返回列表', type: 'error' });
      }
    };

    if (taskFromState) {
      setTask({
        ...taskFromState,
        total: taskFromState.total ?? taskFromState.imageIds?.length ?? 0,
        successCount: taskFromState.successCount ?? 0,
        failedCount: taskFromState.failedCount ?? 0,
        status: taskFromState.status || 'processing',
      });
      setLoadPhase('ready');
    }
    if (taskId) {
      loadTask();
    } else if (!taskFromState) {
      setLoading(false);
      setLoadPhase('error');
    }

    return () => {
      cancelled = true;
    };
  }, [taskId, taskFromState]);

  // 加载图片数据
  useEffect(() => {
    const loadImages = async () => {
      const imageIds = task?.imageIds || imageIdsFromState || [];
      if (imageIds.length === 0) {
        setImageData([]);
        setLoading(false);
        return;
      }

      try {
        const cropImages = await loadCropImagesForTask(
          {
            id: taskId,
            subAccount: task?.subAccount,
            taskId: task?.taskId ?? task?.id ?? taskId,
          },
          imageIds,
        );
        const images = await resolveDetailRowsForImageIds(cropImages, imageIds);
        images.forEach((img) => {
          if ((img.status === 'success' || img.status === 'partial') && (img.croppedUrl || rowHasCropResultTiles(img))) {
            rememberImageCropResult(taskId, img.imageId, img);
          }
        });
        setImageData((prev) => mergeDetailImageRows(prev, images, taskId));

        const progress = computeCropTaskProgress(cropImages, imageIds);
        if (progress.pendingCount === 0) {
          autoProcessTriggeredRef.current = true;
        }
        setTask((prev) =>
          prev
            ? {
                ...prev,
                total: progress.total,
                successCount: progress.successCount,
                failedCount: progress.failedCount,
                status: progress.status,
              }
            : prev,
        );

        if (taskId) {
          taskAPI.update(taskId, {
            total: progress.total,
            successCount: progress.successCount,
            failedCount: progress.failedCount,
            status: progress.status,
          }).catch((err) => {
            console.error('同步任务状态失败:', err);
          });
        }

        setLoading(false);
      } catch (error) {
        console.error('加载图片数据失败:', error);
        setLoading(false);
      }
    };

    if (task?.id && taskId) {
      loadImages();
    } else if (!task?.id && !taskId) {
      setImageData([]);
      setLoading(false);
    }
  }, [
    task?.id,
    task?.imageIds,
    task?.subAccount,
    task?.taskId,
    taskId,
    imageIdsFromState,
    resolveDetailRowsForImageIds,
  ]);

  // 切换任务时清空裁剪图加载失败标记；已终止任务保持 stop 标记
  useEffect(() => {
    clearImageCropResultCache(taskId);
    setCroppedLoadFailed({});
    failedImageIdsRef.current = new Set();
    autoProcessTriggeredRef.current = false;
    terminateRequestedRef.current = isImageCropTaskCancelled(taskId);
    stopImageCropTaskInBackground(taskId);
    setVisibleCropCount(IMAGE_CROP_GROUP_BATCH_SIZE);
  }, [taskId]);

  /** 进入详情：已终止任务补打 cancel 标记并确保服务端 Worker 停止 */
  useEffect(() => {
    if (!taskId || !task) return;
    if (!shouldStopImageCropTask(taskId, task.status, task.result)) return;
    terminateRequestedRef.current = true;
    stopImageCropTaskInBackground(taskId);
    if (!isImageCropTaskCancelled(taskId)) {
      cancelImageCropTask(taskId);
    } else {
      void cancelIpeServerBatch(taskId);
    }
  }, [taskId, task]);

  const pollImageIds = useMemo(
    () => (task?.imageIds || imageIdsFromState || []).filter(Boolean),
    [task?.imageIds, imageIdsFromState],
  );

  const serverWorkerActive = isImageCropServerWorkerActive(task);

  const shouldPollProgress = Boolean(
    task?.id
    && !processing
    && (serverWorkerActive || isTaskInProgress(task.status) || isImageCropBackgroundActive(taskId)),
  );

  const applyPollSnapshot = useCallback(async (snapshot) => {
    if (!task?.id || processingRef.current || manualCropInFlightRef.current) return;

    try {
      const { task: taskData } = await taskAPI.getById(task.id);
      if (!taskData) return;

      const imageIds = taskData.imageIds || pollImageIds;
      if (imageIds.length === 0) return;

      const images = await resolveDetailRowsForImageIds(snapshot.rows || [], imageIds);
      images.forEach((img) => {
        if ((img.status === 'success' || img.status === 'partial') && (img.croppedUrl || rowHasCropResultTiles(img))) {
          rememberImageCropResult(taskId, img.imageId, img);
        }
      });
      setImageData((prev) => mergeDetailImageRows(prev, images, taskId));

      const progress = {
        total: snapshot.total,
        successCount: snapshot.successCount,
        failedCount: snapshot.failedCount,
        pendingCount: snapshot.pendingCount,
        status: snapshot.status,
      };
      if (progress.pendingCount === 0) {
        autoProcessTriggeredRef.current = true;
      }
      setTask((prev) =>
        prev
          ? {
              ...prev,
              ...taskData,
              total: progress.total,
              successCount: progress.successCount,
              failedCount: progress.failedCount,
              status: progress.status,
            }
          : prev,
      );

      if (
        taskData.status !== progress.status
        || (taskData.total ?? taskData.imageIds?.length ?? 0) !== progress.total
        || (taskData.successCount ?? 0) !== progress.successCount
        || (taskData.failedCount ?? 0) !== progress.failedCount
      ) {
        taskAPI.update(task.id, {
          total: progress.total,
          successCount: progress.successCount,
          failedCount: progress.failedCount,
          status: progress.status,
        }).catch((err) => {
          console.error('轮询更新任务状态失败:', err);
        });
      }
    } catch (error) {
      console.error('轮询加载任务数据失败:', error);
    }
  }, [task?.id, pollImageIds, taskId, resolveDetailRowsForImageIds]);

  const handlePollFatalError = useCallback(async (error) => {
    if (!isImageCropFatalPollError(error) && !error?.pollFatal) {
      return;
    }
    autoProcessTriggeredRef.current = true;
    setImageData((prev) => {
      const next = prev.map((img) => (
        img.status === 'pending' || img.status === 'processing'
          ? { ...img, status: 'failed', error: '处理失败' }
          : img
      ));
      const total = next.length;
      const successCount = next.filter((img) => img.status === 'success').length;
      const failedCount = next.filter((img) => img.status === 'failed').length;
      const status = deriveTaskStatus(total, successCount, failedCount, total > 0);
      setTask((prevTask) => (prevTask
        ? { ...prevTask, total, successCount, failedCount, status }
        : prevTask));
      if (taskId) {
        taskAPI.update(taskId, { total, successCount, failedCount, status }).catch(() => {});
      }
      return next;
    });
  }, [deriveTaskStatus, taskId]);

  useImageCropProgress({
    taskId,
    imageIds: pollImageIds,
    enabled: shouldPollProgress,
    intervalMs: 3000,
    onProgress: applyPollSnapshot,
    onPollError: handlePollFatalError,
    silentPollErrors: true,
  });

  /**
   * 将blob URL或data URL转换为base64 data URL
   * 百度AI API需要base64或HTTP/HTTPS URL，不能是blob URL
   */
  const convertToBase64DataUrl = useCallback(async (imageUrl) => {
    // 如果已经是data URL，直接返回
    if (imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    
    // 如果是HTTP/HTTPS URL，直接返回（后端会处理）
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // 相对路径保持 canonical /uploads/...，由后端自行拉取
    if (imageUrl.startsWith('uploads/')) {
      return `/${imageUrl.replace(/^\/+/, '')}`;
    }
    if (imageUrl.startsWith('/uploads/')) {
      return imageUrl;
    }
    
    // 如果是blob URL，需要转换为base64
    if (imageUrl.startsWith('blob:')) {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('转换blob URL失败:', error);
        throw new Error('无法转换blob URL为base64');
      }
    }
    
    // 其他情况，直接返回
    return imageUrl;
  }, []);

  const resolveOriginalUrlForCrop = useCallback(
    async (imageId) => {
      const key = String(imageId);
      const row = imageDataRef.current.find((img) => String(img.imageId) === key);
      const fromRow = row?.originalUrl && String(row.originalUrl).trim();
      if (fromRow) {
        try {
          return await convertToBase64DataUrl(fromRow);
        } catch {
          return fromRow;
        }
      }
      return resolveImageCropOriginalUrl(imageId);
    },
    [convertToBase64DataUrl],
  );

  useEffect(() => {
    registerImageCropResolveOriginalUrl(resolveOriginalUrlForCrop);
    return () => registerImageCropResolveOriginalUrl(null);
  }, [resolveOriginalUrlForCrop]);

  const persistTerminatedTask = useCallback(async () => {
    if (!taskId) return;
    const ids = imageData.map((img) => img.imageId);
    const total = imageData.length;
    let cropImages = [];
    try {
      const res = await cropImageAPI.getByTask(taskId);
      cropImages = res.images || [];
    } catch {
      /* ignore */
    }
    const progress = computeCropTaskProgress(cropImages, ids);
    const patch = {
      status: 'partial',
      total: total || progress.total,
      successCount: progress.successCount,
      failedCount: progress.failedCount,
    };
    await taskAPI.update(taskId, patch);
    await cancelIpeServerBatch(taskId);
    setTask((prev) => (prev ? { ...prev, ...patch } : prev));
    setImageData((prev) =>
      prev.map((img) => (img.status === 'processing' ? { ...img, status: 'pending' } : img)),
    );
  }, [taskId, imageData]);

  useEffect(() => {
    if (!taskId) return undefined;
    const onCancel = (event) => {
      if (String(event.detail?.taskId) !== String(taskId)) return;
      terminateRequestedRef.current = true;
      void persistTerminatedTask().then(() => {
        setToast({ message: '任务已终止', type: 'info' });
      }).catch((err) => console.error('终止任务后保存状态失败:', err));
    };
    window.addEventListener('imageCropTaskCancel', onCancel);
    return () => window.removeEventListener('imageCropTaskCancel', onCancel);
  }, [taskId, persistTerminatedTask]);

  /** 本页执行裁剪（与服务端队列并行；服务端未跑时仍能出图） */
  const processCropImages = useCallback(async (forceReprocess = false) => {
    if (processingRef.current) return false;

    if (forceReprocess) {
      clearImageCropTaskCancelled(taskId);
      terminateRequestedRef.current = false;
    } else if (isImageCropServerWorkerActive(task)) {
      autoProcessTriggeredRef.current = true;
      return false;
    } else if (shouldStopImageCropTask(taskId, task?.status, task?.result) || terminateRequestedRef.current) {
      await persistTerminatedTask();
      setToast({ message: '任务已终止', type: 'info' });
      return false;
    }

    const imageIds = (task?.imageIds || imageIdsFromState || imageData.map((img) => img.imageId)).filter(Boolean);
    if (imageIds.length === 0) return false;

    if (forceReprocess) clearImageCropResultCache(taskId);

    const meta = {
      id: taskId,
      subAccount: task?.subAccount,
      taskId: task?.taskId ?? task?.id ?? taskId,
    };

    setProcessing(true);
    try {
      setImageData((prev) =>
        prev.map((img) => {
          if (!imageIds.some((id) => String(id) === String(img.imageId))) return img;
          if (forceReprocess) {
            const clearedPrints = Array.isArray(img.cropPosition?.prints)
              ? img.cropPosition.prints.map((print) => {
                if (!print || typeof print !== 'object') return print;
                const {
                  croppedImageUrl,
                  cropped_image_url,
                  croppedImageUrlDisplay,
                  cropped_image_url_display,
                  croppedDisplayUrl,
                  ...rest
                } = print;
                return rest;
              })
              : [];
            return {
              ...img,
              status: 'processing',
              croppedUrl: '',
              croppedDisplayUrl: '',
              cropPosition: img.cropPosition
                ? {
                  ...img.cropPosition,
                  prints: clearedPrints,
                }
                : null,
              error: null,
            };
          }
          if (img.status === 'pending') return { ...img, status: 'processing' };
          return img;
        }),
      );

      await withIpeProcessingSlot(
        taskId,
        IMAGE_CROP_NAV_ID,
        () => runImageCropTaskBatch({
          taskId,
          taskMeta: meta,
          imageIds,
          resolveOriginalUrl: resolveOriginalUrlForCrop,
          forceReprocess,
          skipServerSubmit: !forceReprocess && serverWorkerActive,
          showToast,
          onImageProgress: (imageId, patch) => {
            setImageData((prev) => {
              const next = prev.map((img) =>
                String(img.imageId) === String(imageId) ? { ...img, ...patch } : img,
              );
              if (patch?.status === 'success' && patch?.croppedUrl) {
                rememberImageCropResult(taskId, imageId, {
                  imageId,
                  originalUrl: next.find((i) => String(i.imageId) === String(imageId))?.originalUrl,
                  croppedUrl: patch.croppedUrl,
                  status: 'success',
                });
              }
              return mergeDetailImageRows(prev, next, taskId);
            });
          },
          onTaskProgress: (patch) => {
            setTask((prev) => (prev ? { ...prev, ...patch } : prev));
          },
        }),
        {
          isCancelled: () =>
            shouldStopImageCropTask(taskId, task?.status, task?.result) || terminateRequestedRef.current,
        },
      );

      autoProcessTriggeredRef.current = true;
      return true;
    } catch (error) {
      console.error('图片裁剪失败:', error);
      // 必须保持 true，否则 effect 会在 processing=false 后再次自动提交 → 疯狂打接口
      autoProcessTriggeredRef.current = true;
      const presented = presentImageCropApiError(error, { showToast: undefined });
      setImageData((prev) => {
        const next = prev.map((img) => (
          img.status === 'pending' || img.status === 'processing'
            ? { ...img, status: 'failed', error: '处理失败' }
            : img
        ));
        const total = next.length;
        const successCount = next.filter((img) => img.status === 'success').length;
        const failedCount = next.filter((img) => img.status === 'failed').length;
        const status = deriveTaskStatus(total, successCount, failedCount, total > 0);
        setTask((prevTask) => (prevTask ? { ...prevTask, total, successCount, failedCount, status } : prevTask));
        if (taskId) {
          taskAPI.update(taskId, { total, successCount, failedCount, status }).catch(() => {});
        }
        return next;
      });
      // 只在手动重试（force）时用 toast；自动续跑失败只改结果态，不弹窗轰炸
      if (forceReprocess) {
        const failedMessage = resolveImageCropFailedMessage(presented, '裁剪失败，请重试');
        setToast({ message: failedMessage, type: 'error' });
      }
      return false;
    } finally {
      setProcessing(false);
    }
  }, [
    taskId,
    task,
    imageIdsFromState,
    imageData,
    persistTerminatedTask,
    resolveOriginalUrlForCrop,
    showToast,
    serverWorkerActive,
    deriveTaskStatus,
  ]);

  // 进入详情且仍有待裁剪图：自动续跑（已完成/列表 worker 在跑则跳过）
  useEffect(() => {
    if (loading || !taskId || !task || processing || listWorkerActive) return;
    if (imageData.length === 0) return;
    if (shouldStopImageCropTask(taskId, task?.status, task?.result) || terminateRequestedRef.current) return;
    if (autoProcessTriggeredRef.current) return;

    if (isImageCropServerWorkerActive(task)) {
      autoProcessTriggeredRef.current = true;
      return;
    }

    const hasPending = imageData.some(
      (img) => img.status === 'pending' || img.status === 'processing',
    );
    if (!hasPending) {
      autoProcessTriggeredRef.current = true;
      return;
    }

    const uiAllDone = imageData.every(
      (img) => img.status === 'success' || img.status === 'failed',
    );
    if (task.status === 'success' && uiAllDone) return;

    // 已终态（含全部失败）禁止自动再提交；用户可点单张/整批重试
    if (isTerminalCropTaskStatus(task.status)) {
      autoProcessTriggeredRef.current = true;
      return;
    }

    let cancelled = false;
    const run = async () => {
      const imageIds = task?.imageIds || imageIdsFromState || imageData.map((img) => img.imageId);
      const cropImages = await loadCropImagesForTask(
        { id: taskId, subAccount: task?.subAccount, taskId: task?.taskId ?? task?.id ?? taskId },
        imageIds,
      );
      if (cancelled) return;
      const progress = computeCropTaskProgress(cropImages, imageIds);
      const needsCrop = imageIds.some((id) =>
        shouldProcessImageCrop(cropImages.find((r) => String(r?.imageId) === String(id)), {
          forceReprocess: false,
        }),
      );
      if (!needsCrop || progress.pendingCount === 0) {
        autoProcessTriggeredRef.current = true;
        setTask((prev) =>
          prev
            ? {
                ...prev,
                total: progress.total,
                successCount: progress.successCount,
                failedCount: progress.failedCount,
                status: progress.status,
              }
            : prev,
        );
        return;
      }
      void processCropImages(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [loading, task, taskId, imageData, processing, listWorkerActive, imageIdsFromState, processCropImages]);

  useEffect(() => {
    return () => {
      setProcessing(false);
    };
  }, [taskId]);

  useEffect(() => {
    return () => {
      if (!taskId || shouldStopImageCropTask(taskId, task?.status, task?.result)) return;
      if (isImageCropServerWorkerActive(task)) return;
      if (isImageCropBackgroundActive(taskId)) return;
      const imageIds = (task?.imageIds || imageIdsFromState || imageDataRef.current.map((img) => img.imageId)).filter(Boolean);
      if (imageIds.length === 0) return;
      const hasPending = imageDataRef.current.some(
        (img) => img.status === 'pending' || img.status === 'processing',
      );
      if (!hasPending && !processingRef.current) return;
      ensureImageCropTaskInBackground(taskId, imageIds, resolveOriginalUrlForCrop, {
        subAccount: task?.subAccount,
        taskMeta: {
          id: taskId,
          subAccount: task?.subAccount,
          taskId: task?.taskId ?? task?.id ?? taskId,
        },
      });
    };
  }, [taskId, task, imageIdsFromState, resolveOriginalUrlForCrop]);


  const handleImagePreview = useCallback(async (imageUrl, imageName, options = {}) => {
    const raw = String(imageUrl || '').trim();
    if (!raw) return;

    const loadedSrc = String(options.loadedSrc || '').trim();
    const signOptions = {
      displayUrl: options.displayUrl || options.viewUrl || '',
      viewUrl: options.viewUrl || options.displayUrl || '',
    };
    let displayUrl = loadedSrc || signOptions.displayUrl || signOptions.viewUrl || '';
    if (!displayUrl) {
      displayUrl = resolveImmediateSignedSrc(raw, signOptions) || '';
    }
    if (!displayUrl) {
      displayUrl = await resolveSignedDisplayUrl(raw, signOptions) || '';
    }

    openPreview(raw, imageName || '图片预览', {
      ...options,
      id: options.id,
      displayUrl: displayUrl || raw,
      viewUrl: displayUrl || raw,
    });
  }, [openPreview]);

  const handleDownloadImage = useCallback(async (imageUrl, imageId, signOptions = null) => {
    const raw = String(imageUrl || '').trim();
    if (!raw) {
      setToast({ message: '没有可下载的图片', type: 'warning' });
      return;
    }
    try {
      const imageBlob = await resolveImageUrlToBlob(raw, signOptions || undefined);
      const url = URL.createObjectURL(imageBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `图案裁剪-${imageId || 'image'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToast({ message: t('studioChrome.downloadSuccess'), type: 'success' });
    } catch (error) {
      console.error('下载失败:', error);
      setToast({ message: t('studioChrome.downloadFailedRetry'), type: 'error' });
    }
  }, []);

  const syncTaskStatsFromRows = useCallback((rows) => {
    const total = rows.length;
    const successCount = rows.filter((img) => img?.status === 'success').length;
    const failedCount = rows.filter((img) => img?.status === 'failed').length;
    const pendingCount = total - successCount - failedCount;
    const allProcessed = total > 0 && pendingCount <= 0;
    const status = deriveTaskStatus(total, successCount, failedCount, allProcessed);
    setTask((prev) => (prev ? { ...prev, total, successCount, failedCount, status } : prev));
    if (taskId) {
      taskAPI.update(taskId, { total, successCount, failedCount, status }).catch((err) => {
        console.error('同步裁剪任务统计失败:', err);
      });
    }
    return { total, successCount, failedCount, status };
  }, [taskId, deriveTaskStatus]);

  const resolveEditInitialBbox = useCallback((row, printIndex = null) => {
    const cropPosition = normalizeCropPosition(row?.cropPosition);
    if (!cropPosition) return null;

    if (printIndex != null && Array.isArray(cropPosition.prints)) {
      const print = cropPosition.prints.find(
        (item) => Number(item?.printIndex) === Number(printIndex),
      );
      if (print?.bboxNormalized) return print.bboxNormalized;
    }

    return cropPosition.bboxNormalized || extractBboxNormalized(row?.cropPosition);
  }, []);

  const openImageEditModal = useCallback((row, options = {}) => {
    const imageId = row?.imageId;
    const referencePreview = referencePreviewByImageId.get(String(imageId)) || '';
    // 与详情原图 tile 一致：优先受管 canonical，缺省再回退 reference
    const originalUrl = String(row?.originalUrl || referencePreview || '').trim();
    if (!originalUrl) {
      setToast({ message: '暂无可用原图', type: 'warning' });
      return;
    }

    // 与列表一致：优先 API enrich 的新鲜签名 display；受管 canonical 不塞进 displayUrl，交给 SignedImage 现签
    const displayUrl = resolveImageCropOriginalTileDisplayUrl(row, referencePreview);
    setEditModal({
      imageId,
      originalUrl,
      displayUrl,
      title: options.title || '调整裁剪区域',
      initialBbox: resolveEditInitialBbox(row, options.printIndex),
      printIndex: options.printIndex ?? null,
      label: options.label || null,
      confidence: options.confidence,
    });
  }, [referencePreviewByImageId, resolveEditInitialBbox]);

  const closeImageEditModal = useCallback(() => {
    if (editConfirming) return;
    setEditModal(null);
  }, [editConfirming]);

  const handleConfirmManualCrop = useCallback(async (bbox) => {
    if (!taskId || !editModal?.imageId || !bbox || editConfirming) return;

    setEditConfirming(true);
    manualCropInFlightRef.current = true;
    const imageId = editModal.imageId;
    const printIndex = editModal.printIndex;
    const baseRow = imageDataRef.current.find((row) => String(row.imageId) === String(imageId));

    let imageUrl = '';
    try {
      imageUrl = await resolveOriginalUrlForCrop(imageId);
    } catch (error) {
      console.error('解析原图失败:', error);
      setToast({ message: '未找到原图，无法裁剪', type: 'error' });
      setEditConfirming(false);
      manualCropInFlightRef.current = false;
      return;
    }
    if (!imageUrl) {
      setToast({ message: '未找到原图，无法裁剪', type: 'error' });
      setEditConfirming(false);
      manualCropInFlightRef.current = false;
      return;
    }

    setImageData((prev) =>
      prev.map((row) =>
        String(row.imageId) === String(imageId)
          ? { ...row, status: 'processing', error: null }
          : row,
      ),
    );

    try {
      const res = await cropImageAPI.cropByBbox({
        taskId,
        imageId,
        imageUrl,
        bbox,
        confidence: editModal.confidence ?? 1,
        label: editModal.label || '手动框选',
        printIndex,
      });

      const revision = Date.now();
      invalidateManualCropDisplayCache(baseRow, { printIndex });

      const mergedRow = mergeManualCropIntoDetailRow(
        baseRow || { imageId },
        res,
        { printIndex, revision },
      );
      const croppedImageUrl = String(mergedRow?.croppedUrl || '').trim();
      const croppedDisplayUrl = String(mergedRow?.croppedDisplayUrl || croppedImageUrl || '').trim();

      if (!croppedImageUrl && !croppedDisplayUrl && !rowHasSavedCropTiles(mergedRow)) {
        setImageData((prev) => {
          const next = prev.map((row) =>
            String(row.imageId) === String(imageId)
              ? { ...row, status: 'failed', error: '裁剪失败，请重试' }
              : row,
          );
          syncTaskStatsFromRows(next);
          return next;
        });
        setToast({ message: '裁剪失败，请重试', type: 'error' });
        return;
      }

      setImageData((prev) => {
        const next = prev.map((row) => (
          String(row.imageId) === String(imageId) ? mergedRow : row
        ));
        imageDataRef.current = next;
        syncTaskStatsFromRows(next);
        return next;
      });

      rememberImageCropResult(taskId, imageId, {
        imageId,
        originalUrl: mergedRow.originalUrl,
        croppedUrl: croppedImageUrl || croppedDisplayUrl,
        croppedDisplayUrl,
        status: 'success',
        cropPosition: mergedRow.cropPosition,
        resultRevision: revision,
      });
      setCroppedLoadFailed((prev) => {
        const tileKey = printIndex != null
          ? `${imageId}-print-${printIndex}`
          : imageId;
        if (!prev[tileKey] && !prev[imageId]) return prev;
        const next = { ...prev };
        delete next[tileKey];
        delete next[imageId];
        return next;
      });
      setToast({ message: '裁剪已更新', type: 'success' });
      setEditModal(null);
    } catch (error) {
      const presented = presentImageCropApiError(error, { showToast });
      const failedMessage = resolveImageCropFailedMessage(presented, '裁剪失败，请重试');
      setImageData((prev) => {
        const next = prev.map((row) =>
          String(row.imageId) === String(imageId)
            ? { ...row, status: 'failed', error: failedMessage }
            : row,
        );
        imageDataRef.current = next;
        syncTaskStatsFromRows(next);
        return next;
      });
    } finally {
      setEditConfirming(false);
      manualCropInFlightRef.current = false;
    }
  }, [
    taskId,
    editModal,
    editConfirming,
    resolveOriginalUrlForCrop,
    syncTaskStatsFromRows,
    showToast,
  ]);

  const handleImageEdit = useCallback((row, options = {}) => {
    openImageEditModal(row, options);
  }, [openImageEditModal]);

  const handleClearSelection = useCallback(() => {
    setSelectedImageIds(new Set());
  }, []);

  const handleDisableSelected = useCallback(() => {
    if (selectedImageIds.size === 0) return;
    disableKeys(selectedImageIds);
    setSelectedImageIds(new Set());
  }, [selectedImageIds, disableKeys]);

  const handleTileToggle = useCallback((selectKey) => {
    const key = String(selectKey || '');
    if (!key || isDisabled(key)) return;
    setSelectedImageIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, [isDisabled]);

  /** 快速选中仅针对裁剪结果（按原图分组），不包含原图 */
  const quickSelectResultKeys = useMemo(
    () => collectCropDetailResultSelectableTileKeys(displayImageData),
    [displayImageData],
  );

  const quickSelectPositionCount = useMemo(
    () => getCropDetailQuickSelectPositionCount(displayImageData),
    [displayImageData],
  );

  /**
   * 全选：所有已完成印花（不含原图）；
   * 第 N 张：每组原图下第 N 个印花（裁剪效果 N）。
   */
  const handleQuickSelect = useCallback((value) => {
    if (!quickSelectResultKeys.length) return;

    if (value === 'all') {
      const isAllSelected = quickSelectResultKeys.every((id) => selectedImageIds.has(id));
      if (isAllSelected) {
        setSelectedImageIds((prev) => {
          const next = new Set(prev);
          quickSelectResultKeys.forEach((id) => next.delete(id));
          return next;
        });
      } else {
        setSelectedImageIds((prev) => {
          const next = new Set(prev);
          quickSelectResultKeys.forEach((id) => next.add(id));
          return next;
        });
      }
      return;
    }

    if (String(value).startsWith('position-')) {
      const position = parseInt(String(value).replace('position-', ''), 10);
      const positionIndex = position - 1;
      const picked = collectCropDetailQuickSelectKeysAtPosition(displayImageData, positionIndex);
      setSelectedImageIds(new Set(picked));
    }
  }, [displayImageData, quickSelectResultKeys, selectedImageIds]);

  const selectedBridgeSources = useMemo(() => {
    const sources = [];
    const seen = new Set();
    const enabledKeys = new Set(filterEnabledKeys(selectedImageIds));
    enabledKeys.forEach((selectKey) => {
      const entry = resolveCropDetailSelectionEntry(selectKey, displayImageData);
      if (!entry?.url || seen.has(entry.url)) return;
      seen.add(entry.url);
      const preview = String(entry.displayUrl || '').trim();
      const canonicalUrl = entry.url;
      sources.push({
        id: entry.sourceId,
        name: entry.name,
        url: canonicalUrl,
        imageId: entry.imageId,
        previewUrl: preview || undefined,
        viewUrl: preview || undefined,
        displayUrl: preview || undefined,
      });
    });
    return sources;
  }, [displayImageData, filterEnabledKeys, selectedImageIds]);

  const allBridgeSources = useMemo(() => {
    const sources = [];
    const seen = new Set();
    const enabledKeys = new Set(filterEnabledKeys(quickSelectResultKeys));
    enabledKeys.forEach((selectKey) => {
      const entry = resolveCropDetailSelectionEntry(selectKey, displayImageData);
      if (!entry?.url || seen.has(entry.url)) return;
      seen.add(entry.url);
      const preview = String(entry.displayUrl || '').trim();
      const canonicalUrl = entry.url;
      sources.push({
        id: entry.sourceId,
        name: entry.name,
        url: canonicalUrl,
        imageId: entry.imageId,
        previewUrl: preview || undefined,
        viewUrl: preview || undefined,
        displayUrl: preview || undefined,
      });
    });
    return sources;
  }, [displayImageData, filterEnabledKeys, quickSelectResultKeys]);

  const resolveLaunchContext = useCallback(async (targetNavId) => {
    const targetNav = String(targetNavId || '').trim();
    const zeroSelectionListingIngest = (
      targetNav === 'platformListing'
      && selectedBridgeSources.length === 0
    );
    const launchSources = zeroSelectionListingIngest ? allBridgeSources : selectedBridgeSources;
    if (launchSources.length === 0) {
      showToast(
        zeroSelectionListingIngest ? '暂无可入库商品' : '请先选择图片',
        'warning',
      );
      return null;
    }
    if (targetNav === 'platformListing') {
      return {
        sources: launchSources,
        listingEnrich: {
          kind: 'crop',
          options: { displayImageData },
        },
        fromCropTaskId: taskId || '',
      };
    }
    return {
      sources: launchSources,
      fromCropTaskId: taskId || '',
    };
  }, [allBridgeSources, displayImageData, selectedBridgeSources, showToast, taskId]);

  const quickBridge = useDetailQuickToolBridge({
    setToast: (payload) => setToast(payload),
    enablePodPicker: true,
    resolveLaunchContext,
    sourceNavId: IMAGE_CROP_NAV_ID,
  });

  const collectCropResultExportSources = useCallback((preferSelected) => {
    const keys = preferSelected && selectedImageIds.size > 0
      ? [...selectedImageIds].filter((key) => String(key).startsWith('crop-detail-result:'))
      : quickSelectResultKeys;
    const sources = [];
    const seen = new Set();
    const enabledKeys = new Set(filterEnabledKeys(keys));
    enabledKeys.forEach((selectKey) => {
      const entry = resolveCropDetailSelectionEntry(selectKey, displayImageData);
      if (!entry?.url || seen.has(entry.url)) return;
      seen.add(entry.url);
      sources.push({
        id: entry.sourceId,
        name: entry.name,
        url: entry.url,
        imageId: entry.imageId,
      });
    });
    return sources;
  }, [displayImageData, filterEnabledKeys, quickSelectResultKeys, selectedImageIds]);

  const handleExportByTemplate = useCallback(() => {
    openExportPicker(async () => {
      const selectedResults = collectCropResultExportSources(true);
      const sources = selectedResults.length > 0
        ? selectedResults
        : collectCropResultExportSources(false);
      if (sources.length === 0) {
        throw new Error('暂无可导出的裁剪结果图');
      }
      return buildSimpleImageExportRows(sources, {
        columnMode: resolveSimpleExportColumnMode('imageCrop'),
      });
    });
  }, [collectCropResultExportSources, openExportPicker]);

  const handleBulkDownloadSelected = useCallback(async () => {
    const items = [];
    const enabledKeys = filterEnabledKeys(selectedImageIds);
    enabledKeys.forEach((selectKey) => {
      const entry = resolveCropDetailSelectionEntry(selectKey, imageData);
      if (!entry?.url) return;
      items.push({
        url: entry.url,
        carouselStem: entry.carouselStem || entry.name,
        templateStem: task?.subAccount || String(task?.id || ''),
        stampStem: '',
      });
    });
    if (items.length === 0) {
      showToast(t('studioChrome.pickDownloadableFirst'), 'warning');
      return;
    }
    if (items.length === 1) {
      setBulkZipBusy(true);
      try {
        const [item] = items;
        await handleDownloadImage(item.url, item.carouselStem);
        showToast(t('studioChrome.downloadSuccess'), 'success');
      } catch (error) {
        console.error(error);
        showToast(t('studioChrome.downloadFailedRetry'), 'error');
      } finally {
        setBulkZipBusy(false);
      }
      return;
    }
    setPendingBulkDownloadItems(items);
    setIsDownloadModalOpen(true);
  }, [filterEnabledKeys, imageData, selectedImageIds, task, handleDownloadImage, showToast]);

  // 重新裁剪：自动逐张识别裁剪
  const handleReCropClick = useCallback(() => {
    if (isCropBusy) return;
    const hasImages = imageData.some((img) => !!img?.originalUrl);
    if (!hasImages) {
      setToast({ message: '未找到可裁剪图片', type: 'warning' });
      return;
    }
    stopImageCropTaskInBackground(taskId);
    autoProcessTriggeredRef.current = true;
    setToast({ message: '开始自动重新裁剪…', type: 'info' });
    processCropImages(true);
  }, [isCropBusy, imageData, processCropImages, taskId]);

  const handleRetryImage = useCallback(async (img) => {
    if (!taskId || !img?.imageId || retryingImageId || isCropBusy) return;

    clearImageCropTaskCancelled(taskId);
    terminateRequestedRef.current = false;

    let imageUrl = '';
    try {
      imageUrl = await resolveOriginalUrlForCrop(img.imageId);
    } catch (error) {
      console.error('解析原图失败:', error);
      setToast({ message: '未找到原图，无法重试', type: 'error' });
      return;
    }
    if (!imageUrl) {
      setToast({ message: '未找到原图，无法重试', type: 'error' });
      return;
    }

    const imageId = img.imageId;
    failedImageIdsRef.current.delete(imageId);
    setRetryingImageId(imageId);
    setImageData((prev) =>
      prev.map((row) =>
        String(row.imageId) === String(imageId)
          ? { ...row, status: 'processing', error: null }
          : row,
      ),
    );

    try {
      const result = await retryImageCropItem(
        {
          taskId,
          imageId,
          imageUrl,
          cropPosition: img.cropPosition,
        },
        { showToast },
      );

      setImageData((prev) => {
        const next = prev.map((row) => {
          if (String(row.imageId) !== String(imageId)) return row;
          const mapped = mapCropRowToDetailRow(imageId, {
            imageId,
            status: result.status === 'success' ? 'success' : 'failed',
            croppedImageUrl: result.croppedImageUrl || '',
            croppedImageUrlDisplay: result.croppedDisplayUrl || '',
            originalImageUrl: row.originalUrl,
            error: result.error || null,
            pipeline: result.pipeline,
            usedFallback: result.usedFallback,
            cropPosition: result.cropPosition ?? row.cropPosition,
          }, row.originalUrl);
          return {
            ...row,
            ...mapped,
          };
        });
        syncTaskStatsFromRows(next);
        return mergeDetailImageRows(prev, next, taskId);
      });

      if (result.status === 'success' && result.croppedImageUrl) {
        rememberImageCropResult(taskId, imageId, {
          imageId,
          originalUrl: img.originalUrl,
          croppedUrl: result.croppedImageUrl,
          croppedDisplayUrl: result.croppedDisplayUrl,
          status: 'success',
        });
        setCroppedLoadFailed((prev) => {
          if (!prev[imageId]) return prev;
          const next = { ...prev };
          delete next[imageId];
          return next;
        });
        setToast({ message: '重试成功', type: 'success' });
      } else if (result.status === 'failed') {
        failedImageIdsRef.current.add(imageId);
        setToast({
          message: sanitizeIpeUserMessage(result.error, '重试失败'),
          type: 'error',
        });
      }
    } catch (error) {
      const presented = presentImageCropApiError(error, { showToast });
      const failedMessage = resolveImageCropFailedMessage(presented, '重试失败');
      failedImageIdsRef.current.add(imageId);
      setImageData((prev) => {
        const next = prev.map((row) =>
          String(row.imageId) === String(imageId)
            ? { ...row, status: 'failed', error: failedMessage }
            : row,
        );
        syncTaskStatsFromRows(next);
        return mergeDetailImageRows(prev, next, taskId);
      });
    } finally {
      setRetryingImageId(null);
    }
  }, [
    taskId,
    retryingImageId,
    isCropBusy,
    resolveOriginalUrlForCrop,
    syncTaskStatsFromRows,
    showToast,
  ]);

  const handleConfirmCropDownload = async (options) => {
    const rows = downloadTargetRows || imageData;
    const items = (pendingBulkDownloadItems || collectCropDownloadItems(rows)).map((item) => ({
      ...item,
      templateStem: task?.subAccount || String(task?.id || ''),
    }));
    if (items.length === 0) {
      setToast({ message: '没有可下载的图片', type: 'error' });
      return;
    }
    try {
      await performFlatImageListZipDownload({
        items,
        options,
        zipFileName: task ? `图片裁剪-${task.id || 'download'}.zip` : '图片裁剪.zip',
      });
      setIsDownloadModalOpen(false);
      setDownloadTargetRows(null);
      setPendingBulkDownloadItems(null);
      setToast({ message: t('studioChrome.downloadSuccess'), type: 'success' });
    } catch (error) {
      console.error('下载失败:', error);
      if (error.message && error.message.includes('Cannot find module')) {
        setToast({ message: '请先安装JSZip库：npm install jszip', type: 'error' });
      } else {
        setToast({ message: t('studioChrome.downloadFailedRetry'), type: 'error' });
      }
    }
  };

  const heroMeta = useMemo(() => {
    if (!task) return null;
    return buildBatchTaskHeroMeta({
      ...task,
      total: viewStats.total,
      successCount: viewStats.successCount,
      failedCount: viewStats.failedCount,
      status: viewStats.status,
    });
  }, [task, viewStats]);

  /** 快捷功能弹窗打开时隐藏底部多选栏，避免盖住弹窗（与主图创作 AI 文案一致） */
  const blockingModalOpen = Boolean(
    quickBridge.isModalOpen
    || isDownloadModalOpen
    || editModal
    || isExportUiOpen,
  );

  const detailLoadingHint = buildDetailLoadingHint(loadPhase);

  // 详情页滚动容器为 .img2img-detail__main；task 未就绪时 main 未挂载，挂载后回填
  useEffect(() => {
    setMainScrollRoot(mainScrollRef.current || null);
  }, [task, loading, loadPhase]);

  useDetailPageLargeListMode(displayImageData.length > DETAIL_PAGE_LARGE_LIST_THRESHOLD);

  const showCropIncremental = displayImageData.length > IMAGE_CROP_GROUP_BATCH_SIZE;
  const hasMoreCrop = visibleCropCount < displayImageData.length;

  useArmedScrollLoadMore({
    enabled: showCropIncremental && hasMoreCrop,
    hasMore: hasMoreCrop,
    loadingMore: false,
    onLoadMore: () => setVisibleCropCount((count) => (
      Math.min(count + IMAGE_CROP_GROUP_BATCH_SIZE, displayImageData.length)
    )),
    sentinelRef: loadMoreSentinelRef,
    scrollRoot: mainScrollRoot,
  });

  if (!task) {
    return (
      <WorkbenchDetailPageShell
        shellClassName={IMAGE_CROP_DETAIL_SHELL_CLASS}
        pageClassName={IMAGE_CROP_DETAIL_PAGE_CLASS}
        onOpenAccountCenter={openAccountCenter}
      >
        <WorkbenchDetailPageHero
          variant="goods"
          backLabel={t('studioChrome.backToCrop')}
          onBack={goBack}
          title={t('studioChrome.cropDetailTitle')}
          metaItems={[{ label: t('studioChrome.statusLabel'), value: loadPhase === 'error' ? t('studioChrome.loadFailedShort') : t('studioChrome.loading') }]}
          progressHint={loadPhase === 'error' ? undefined : detailLoadingHint}
          failureHint={loadPhase === 'error' ? t('studioToast.taskLoadFail') : undefined}
        />
        <main className="img2img-detail__main">
          {loadPhase === 'error' ? (
            <div className="img2img-detail__loading img2img-detail__loading--error">
              <span>{t('studioToast.taskLoadFail')}</span>
              <button type="button" className="img2img-detail__reuse-btn" onClick={() => window.location.reload()}>
                {t('studioChrome.reload')}
              </button>
            </div>
          ) : (
            <div className="img2img-detail__loading">
              <div className="img2img-detail__loading-spinner" aria-hidden="true" />
              <span>{detailLoadingHint}</span>
            </div>
          )}
        </main>
      </WorkbenchDetailPageShell>
    );
  }

  return (
    <WorkbenchDetailPageShell
      shellClassName={IMAGE_CROP_DETAIL_SHELL_CLASS}
      pageClassName={IMAGE_CROP_DETAIL_PAGE_CLASS}
      onOpenAccountCenter={openAccountCenter}
    >
      <WorkbenchDetailPageHero
        variant="goods"
        backLabel="返回图案裁剪"
        onBack={goBack}
        title={t('studioChrome.cropDetailTitle')}
        metaItems={heroMeta?.metaItems}
        status={heroMeta?.status}
      />

      <DetailPageScrollContext.Provider value={mainScrollRoot}>
      <main ref={mainScrollRef} className="img2img-detail__main">
        <section className="img2img-detail__section img2img-detail__section--gallery">
          <DetailQuickToolBridgeToolbar
            disabled={loading}
            busy={quickBridge.busy}
            currentNavId="imageCrop"
            onSelectNav={quickBridge.onSelectNav}
            onExportByTemplate={handleExportByTemplate}
            extra={(
              <button
                type="button"
                className="sourcing-tool-bridge__group-btn"
                onClick={handleReCropClick}
                disabled={isCropBusy || imageData.length === 0}
              >
                {isCropBusy ? '处理中...' : '重新裁剪'}
              </button>
            )}
          />

          {loading ? (
            <div className="img2img-detail__loading">
              <div className="img2img-detail__loading-spinner" aria-hidden="true" />
              <span>{loadPhase === 'slow' ? '图片资源较多，加载较慢，请稍候…' : '正在加载图片资源…'}</span>
            </div>
          ) : displayImageData.length === 0 ? (
            <div className="funding-empty-state">
              <p>{t('studioChrome.noImageData')}</p>
            </div>
          ) : (
            <div className="img2img-detail__gallery img2img-detail__gallery--paired img2img-detail__gallery--source-pairs-grid goods-detail__gallery">
              {displayImageData.slice(0, visibleCropCount).map((img, index) => {
                const rowIndex = index + 1;
                const referencePreview = referencePreviewByImageId.get(String(img.imageId)) || '';
                const originalUrl = String(img.originalUrl || referencePreview || '').trim();
                // 与裁剪效果一致：优先 API enrich 的新鲜签名；受管路径交给 SignedImage 现签
                const originalDisplayUrl = resolveImageCropOriginalTileDisplayUrl(img, referencePreview);
                const croppedReady = img.status === 'success' && (
                  Boolean(img.croppedUrl) || rowHasSavedCropTiles(img)
                );
                const croppedState = img.status === 'processing'
                  ? 'loading'
                  : img.status === 'failed'
                    ? 'error'
                    : img.status === 'pending'
                      ? 'pending'
                      : croppedReady
                        ? 'ready'
                        : 'pending';

                const originalState = originalUrl ? 'ready' : 'pending';
                const originalSelectKey = buildCropDetailOriginalSelectKey(img.imageId);

                return (
                  <article
                    key={img.imageId}
                    className="img2img-detail__card img2img-detail__card--expanded ai-image-detail__group-card"
                  >
                    <div className="img2img-detail__card-body">
                      <ImageCropDetailMediaTile
                        label={`原图 ${rowIndex}`}
                        url={originalUrl || ''}
                        displayUrl={originalDisplayUrl || ''}
                        imageId={img.imageId}
                        imageSpaceId={img.imageId}
                        imageName={String(img.name || img.imageName || `原图_${img.imageId || rowIndex}`)}
                        navId={IMAGE_CROP_NAV_ID}
                        taskId={taskId}
                        alt={`原图 ${rowIndex}`}
                        state={originalState}
                        selected={selectedImageIds.has(originalSelectKey)}
                        selectable
                        selectKey={originalSelectKey}
                        disabled={isDisabled(originalSelectKey)}
                        onRestoreDisabled={() => restoreKey(originalSelectKey)}
                        onToggleSelect={handleTileToggle}
                        onPreview={(url, name, extra) => handleImagePreview(url, name, {
                          ...extra,
                          id: `${img.imageId}-original`,
                          displayUrl: originalDisplayUrl || undefined,
                          viewUrl: originalDisplayUrl || undefined,
                        })}
                        onDownload={() => handleDownloadImage(originalUrl, img.imageId)}
                      />
                      <div className="img2img-detail__card-arrow" aria-hidden="true">
                        <ArrowRight size={18} />
                      </div>
                      <ImageCropDetailCropResults
                        img={img}
                        rowIndex={rowIndex}
                        croppedState={croppedState}
                        croppedLoadFailed={croppedLoadFailed}
                        setCroppedLoadFailed={setCroppedLoadFailed}
                        referencePreviewByImageId={referencePreviewByImageId}
                        selectedTileKeys={selectedImageIds}
                        isDisabled={isDisabled}
                        onRestoreDisabled={restoreKey}
                        onToggleSelect={handleTileToggle}
                        onPreview={handleImagePreview}
                        onEdit={handleImageEdit}
                        onDownload={handleDownloadImage}
                        onRetry={handleRetryImage}
                        retrying={String(retryingImageId) === String(img.imageId)}
                        taskId={taskId}
                        outputIndexByTileKey={cropOutputIndexByTileKey}
                      />
                    </div>
                  </article>
                );
              })}
              {showCropIncremental ? (
                <IpeLazyListSentinel
                  sentinelRef={loadMoreSentinelRef}
                  loadingMore={false}
                  hasMore={hasMoreCrop}
                  loadedCount={Math.min(visibleCropCount, displayImageData.length)}
                  totalItems={displayImageData.length}
                  itemUnit="张"
                  className="image-crop-detail__load-more-sentinel"
                />
              ) : null}
            </div>
          )}
        </section>
      </main>
      </DetailPageScrollContext.Provider>

      <IpeImagePreviewOverlay
        preview={previewImage}
        scale={previewScale}
        setScale={setPreviewScale}
        containerRef={previewContainerRef}
        onClose={closePreview}
      />

      <DetailQuickToolBridgeModals
        bridge={quickBridge}
        copywritingSelectedCountFallback={selectedImageIds.size}
      />

      <IpeDownloadOptionsModal
        isOpen={isDownloadModalOpen}
        onClose={() => {
          setIsDownloadModalOpen(false);
          setDownloadTargetRows(null);
          setPendingBulkDownloadItems(null);
        }}
        onConfirm={handleConfirmCropDownload}
      />

      <ImageCropAdjustModal
        open={Boolean(editModal)}
        title={editModal?.title || '调整裁剪区域'}
        originalUrl={editModal?.originalUrl || ''}
        displayUrl={editModal?.displayUrl || ''}
        imageId={editModal?.imageId || ''}
        initialBbox={editModal?.initialBbox || null}
        confirming={editConfirming}
        onClose={closeImageEditModal}
        onConfirm={handleConfirmManualCrop}
      />

      {toast && (
        <Toast
          key={`${toast.message}-${toast.type}`}
          message={toast.message}
          type={toast.type}
          onClose={handleToastClose}
        />
      )}

      <IpeGallerySelectionBar
        visible={selectedImageIds.size > 0 && !blockingModalOpen}
        countLabel={`已选 ${selectedImageIds.size} 张`}
        showSelectAll={false}
        onSelectAll={() => handleQuickSelect('all')}
        onClearSelection={handleClearSelection}
        onDisable={handleDisableSelected}
        disableDisabled={selectedImageIds.size === 0}
        onDownload={handleBulkDownloadSelected}
        downloadBusy={bulkZipBusy}
        downloadDisabled={filterEnabledKeys(selectedImageIds).length === 0 || loading}
      >
        <GoodsDetailQuickSelectMenu
          totalCount={quickSelectPositionCount}
          onSelect={handleQuickSelect}
        />
      </IpeGallerySelectionBar>

      {ListingTemplateExportModals}
    </WorkbenchDetailPageShell>
  );
}
